import { useEffect, useState } from "react";
import { useParams, useSearchParams } from "react-router-dom";
import axios from "axios";
import { API } from "@/lib/api";
import { fmtMoney } from "@/lib/format";
import { Button } from "@/components/ui/button";
import { Wrench, CreditCard, CheckCircle2, Loader2, AlertCircle } from "lucide-react";
import StatusBadge from "@/components/StatusBadge";

export default function PublicInvoice() {
  const { token } = useParams();
  const [searchParams, setSearchParams] = useSearchParams();
  const sessionId = searchParams.get("session_id");
  const [data, setData] = useState(null);
  const [err, setErr] = useState("");
  const [paying, setPaying] = useState(false);
  const [polling, setPolling] = useState(false);
  const [paid, setPaid] = useState(false);

  const load = async () => {
    try {
      const { data } = await axios.get(`${API}/public/invoices/${token}`);
      setData(data);
      if (data.invoice.status === "paid") setPaid(true);
    } catch {
      setErr("This invoice isn't available.");
    }
  };

  useEffect(() => { load(); }, [token]); // eslint-disable-line

  useEffect(() => {
    if (!sessionId) return;
    let cancelled = false, attempts = 0;
    setPolling(true);
    const tick = async () => {
      if (cancelled || attempts >= 10) { setPolling(false); return; }
      attempts++;
      try {
        const { data } = await axios.get(`${API}/public/payments/status/${token}/${sessionId}`);
        if (data.payment_status === "paid") {
          setPaid(true); setPolling(false); setSearchParams({});
          await load(); return;
        }
        setTimeout(tick, 2000);
      } catch { setTimeout(tick, 2000); }
    };
    tick();
    return () => { cancelled = true; };
  }, [sessionId, token]); // eslint-disable-line

  const payNow = async () => {
    setPaying(true);
    try {
      const { data } = await axios.post(`${API}/public/invoices/${token}/checkout`, { origin_url: window.location.origin });
      window.location.href = data.url;
    } catch { setPaying(false); }
  };

  if (err) return <PublicShell><div className="text-center text-slate-600">{err}</div></PublicShell>;
  if (!data) return <PublicShell><div className="text-center text-slate-500">Loading…</div></PublicShell>;

  const { invoice, customer, company, can_pay, pay_reason } = data;
  const isPaid = invoice.status === "paid" || paid;

  return (
    <PublicShell>
      <div className="bg-white rounded-2xl border border-slate-200 p-5 sm:p-8">
        <div className="flex items-start justify-between gap-3 flex-wrap">
          <div>
            <div className="text-xs text-slate-500 font-bold uppercase tracking-wide">Invoice {invoice.number}</div>
            <h1 className="text-3xl font-bold mt-1" style={{ fontFamily: "Outfit" }}>{fmtMoney(invoice.total_amount)}</h1>
            <p className="text-slate-500 text-sm mt-1">From <b>{company.name}</b> · For {customer.name}</p>
          </div>
          <StatusBadge status={invoice.status} />
        </div>

        <div className="mt-5 border-t-2 border-slate-900">
          <div className="grid grid-cols-12 text-xs font-bold uppercase text-slate-500 py-2.5 tracking-wide">
            <div className="col-span-7">Description</div>
            <div className="col-span-3 text-right">Qty × Rate</div>
            <div className="col-span-2 text-right">Total</div>
          </div>
          {invoice.items.map((it, i) => (
            <div key={i} className="grid grid-cols-12 py-2.5 text-sm border-t border-slate-100">
              <div className="col-span-7 font-medium">{it.description}</div>
              <div className="col-span-3 text-right text-slate-600">{it.quantity} × {fmtMoney(it.unit_price)}</div>
              <div className="col-span-2 text-right font-semibold">{fmtMoney(Number(it.quantity) * Number(it.unit_price))}</div>
            </div>
          ))}
        </div>

        <div className="mt-3 flex items-center justify-between">
          <div className="text-slate-500 font-semibold">Total Due</div>
          <div className="text-3xl font-bold" style={{ fontFamily: "Outfit" }}>{fmtMoney(invoice.total_amount)}</div>
        </div>

        {polling && (
          <div className="mt-5 rounded-xl bg-blue-50 border border-blue-200 px-4 py-3 flex items-center gap-2 text-sm text-blue-900" data-testid="public-checking">
            <Loader2 className="w-4 h-4 animate-spin" /> Confirming your payment…
          </div>
        )}

        {isPaid ? (
          <div className="mt-5 rounded-2xl bg-green-50 border border-green-200 p-5 text-center" data-testid="public-paid">
            <CheckCircle2 className="w-10 h-10 text-green-600 mx-auto" />
            <h2 className="text-lg font-bold mt-2">Payment received — thank you!</h2>
            <p className="text-sm text-slate-600 mt-1">{company.name} has been notified.</p>
          </div>
        ) : (
          <div className="mt-6">
            {can_pay ? (
              <Button onClick={payNow} disabled={paying} className="w-full h-14 bg-green-600 hover:bg-green-700 text-base font-bold rounded-xl" data-testid="public-pay">
                {paying ? <><Loader2 className="w-5 h-5 mr-2 animate-spin" /> Opening Stripe…</> : <><CreditCard className="w-5 h-5 mr-2" /> Pay {fmtMoney(invoice.total_amount)} Now</>}
              </Button>
            ) : (
              <div className="rounded-xl bg-amber-50 border border-amber-200 px-4 py-3 flex items-start gap-2 text-sm text-amber-900" data-testid="public-no-pay">
                <AlertCircle className="w-4 h-4 mt-0.5 flex-shrink-0" />
                <div>
                  <b>Online payment isn't available yet.</b>
                  <div className="text-xs mt-1">{pay_reason || "Contact the plumbing company to settle this invoice."}</div>
                </div>
              </div>
            )}
            <p className="text-xs text-slate-400 text-center mt-3">Secure checkout via Stripe · Cards & Apple/Google Pay</p>
          </div>
        )}
      </div>
    </PublicShell>
  );
}

function PublicShell({ children }) {
  return (
    <div className="min-h-screen bg-slate-50 px-4 py-8">
      <div className="max-w-2xl mx-auto">
        <div className="flex items-center justify-center gap-2 mb-6">
          <div className="h-9 w-9 rounded-xl bg-blue-600 flex items-center justify-center">
            <Wrench className="w-5 h-5 text-white" />
          </div>
          <span className="text-xl font-bold" style={{ fontFamily: "Outfit" }}>PlumberHQ</span>
        </div>
        {children}
        <p className="text-center text-xs text-slate-400 mt-6">Sent via PlumberHQ · Secure link</p>
      </div>
    </div>
  );
}
